<template>
  <div>
    <div v-if="isCustomInput">
      <div class="login_popup_input">
        <label class="custom-label">
          {{ label }}
        </label>
        <a-input
          class="ant-input-custom"
          :class="required === true ? 'ant-required' : ''"
          :value="value"
          :placeholder="placeholder"
          :type="type"
          :maxlength="maxlength"
          :disabled="disabled"
          @blur="handleBlur"
          @input="handleChange"
          :readOnly="readOnly || isAgencyThirdView"
          :allow-clear="false"
          :title="title"
        />
      </div>
    </div>

    <div v-if="!isCustomInput">
      <a-row type="flex">
        <a-col v-if="label && isLinkLabel" :span="colLabel" class="custom-label">
          <a>
            <u>{{ label }}</u>
          </a>
        </a-col>
        <a-col style="padding-bottom: 7px" v-else-if="label" :span="colLabel" class="custom-label">
          {{ label }}
        </a-col>
        <a-col
          v-else
          :span="colLabel"
          class="custom-no-label"
          :style="{ height: currentLocale === 'en' ? '' : '12px' }"
        />
        <a-col :span="colSpan" style="display: inherit; width: 100%">
          <div v-if="required === true" class="require">
            {{ require }}
          </div>
          <div v-else class="no-require" />
          <a-form-item :rules="[{ required: required, message: messageError }]">
            <a-input
              class="ant-input-custom"
              :class="[
                shortFormInput === true ? 'short_form_input' : '',
                disabled ? 'ant-input-custom-disabled' : '',
                isValid ? 'ant-input-custom-valid' : ''
              ]"
              :title="title"
              :name="name"
              :value="value"
              :placeholder="placeholder"
              :type="type"
              @blur="handleBlur"
              :maxlength="maxlength"
              :disabled="disabled"
              @input="handleChange"
              :allow-clear="false"
              :readOnly="ignoreLock ? false : readOnly || isLock || isAgencyThirdView"
            >
              <template v-if="removed" #suffix>
                <a-tooltip @click="removeValue">
                  <MinusOutlined />
                </a-tooltip>
              </template>
            </a-input>
            <p v-if="inputErrorMessage && isError" class="error-message">
              {{ $t(inputFieldErrorMsg) }}
            </p>
          </a-form-item>
        </a-col>
      </a-row>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, inject, ref, watch } from 'vue'
import { useI18n } from 'vue3-i18n'
import { MinusOutlined } from '@ant-design/icons-vue'
import {
  formatPhoneNumber,
  formatPolicyAndBranchNo,
  convertToHalfWidth,
  formatDigitOnly,
  formatZipCode,
  formatPhNo
} from '../utils/newUtils/FormatUtils'
import { useStore } from 'vuex'

const props = defineProps({
  name: { type: String },
  time: { type: String },
  label: { type: String, default: '' },
  placeholder: { type: String, default: '' },
  maxlength: { type: Number },
  type: { type: String, default: '' },
  disabled: { type: Boolean },
  readOnly: { type: Boolean },
  value: { type: [String, Number], default: '' },
  colSpan: { type: String },
  colLabel: { type: String },
  required: { type: Boolean, default: false },
  onchange: { type: Function },
  showImg: { type: Boolean },
  srcImg: { type: String },
  onChangeImg: { type: Function },
  messageError: { type: String, default: '' },
  imgName: { type: String },
  removed: { type: Boolean, default: false },
  isLinkLabel: { type: Boolean, default: false },
  hyperlink: { type: String },
  isCustomInput: { type: Boolean, default: false },
  shortFormInput: { type: Boolean, default: false },
  searchPopUp: { type: Boolean, default: false },
  widthForm: { type: Boolean, default: false },
  isZipCode: { type: Boolean, default: false },
  isPhoneNumber: { type: Boolean, default: false },
  isPolicyAndBranchNo: { type: Boolean, default: false },
  isNumberPlate: { type: Boolean, default: false },
  isReportingParty: { type: Boolean, default: false },
  isReportingPartyPhone: { type: Boolean, default: false },
  inputFieldErrorMsg: { type: String, default: '' },
  isError: { type: Boolean, default: false },
  isValid: { type: Boolean, default: false },
  allowClear: { type: Boolean, default: true },
  ignoreLock: { type: Boolean, default: false },
  isAge: { type: Boolean, default: false },
  title: { type: String, default: '' }
})

const inputValue = ref(props.value) // Use a local ref for inputValue
const i18n = useI18n()
const store = useStore()
const language = localStorage.getItem('lang') || i18n.getLocale()

const searchPopUp = ref(props.searchPopUp)
const widthForm = ref(props.widthForm)
const marginBottomDyn = ref('')
const emits = defineEmits(['update:value', 'remove-item', 'blur', 'input'])
const isLock = inject('isLock', false)
const isAgencyThirdView = inject('isAgencyThirdView', false)
const currentLocale = computed(() => store.getters['common/getCurrentLocale'])
const widthDyn = searchPopUp.value && widthForm.value ? '16.5%' : '50%'
const alignDyn = searchPopUp.value ? 'flex-end' : 'flex-start'
const marginDyn = searchPopUp.value ? '20px' : '0px'
const inputErrorMessage = ref(!props.isError)
const handleChange = (event: any) => {
  const inputValue = (event.target as HTMLInputElement).value
  let maskedValue = ''
  if (props.isZipCode) {
    maskedValue = formatZipCode(inputValue)
    event.target.value = maskedValue
  } else if (props.isPhoneNumber) {
    let outputDigit = formatDigitOnly(inputValue)
    // Maximum 14 DIGITS, excluding "-"
    outputDigit = outputDigit.slice(0, 14)
    maskedValue = outputDigit.length > 0 ? formatPhoneNumber(outputDigit) : outputDigit
    if (
      !maskedValue &&
      event.inputType !== 'deleteContentBackward' &&
      event.pointerType !== 'mouse'
    ) {
      inputErrorMessage.value = true
    } else {
      inputErrorMessage.value = false
    }
    event.target.value = maskedValue
  } else if (props.isPolicyAndBranchNo) {
    const outputDigit = formatPolicyAndBranchNo(formatDigitOnly(inputValue))
    maskedValue = outputDigit.length > 0 ? formatPhoneNumber(outputDigit) : outputDigit
    if (
      !maskedValue &&
      event.inputType !== 'deleteContentBackward' &&
      event.pointerType !== 'mouse'
    ) {
      inputErrorMessage.value = true
    } else {
      inputErrorMessage.value = false
    }
    event.target.value = maskedValue
    event.target.value = maskedValue
  } else if (props.isNumberPlate) {
    maskedValue = convertToHalfWidth(inputValue)
  } else if (props.isReportingParty) {
    maskedValue = convertToHalfWidth(inputValue)
  } else if (props.isReportingPartyPhone) {
    maskedValue = convertToHalfWidth(inputValue)
  } else if (props.isAge) {
    maskedValue = formatDigitOnly(inputValue).slice(0, 3)
    event.target.value = maskedValue
  } else {
    maskedValue = inputValue
  }
  emits('update:value', maskedValue)
  emits('input', event)
}

const removeValue = () => {
  inputValue.value = ''
  inputErrorMessage.value = false
  emits('update:value', '') // Ensure the value is also cleared in the parent component
  emits('remove-item') // Emit a custom event to indicate item removal
}

const handleBlur = () => {
  emits('blur')
}
watch(
  () => props.value,
  (newValue) => {
    inputValue.value = newValue
  }
)
watch(props, () => {
  if (props.isError) inputErrorMessage.value = false
})
const noLabelInputClass = (locale: string) => {
  if (locale === 'ja') {
    marginBottomDyn.value = '4px'
  } else if (locale === 'en') {
    marginBottomDyn.value = '-1px'
  }
}

noLabelInputClass(language)

watch(
  () => i18n.getLocale(),
  (newLocale) => {
    // console.log('Locale changed to:', newLocale)
    noLabelInputClass(newLocale)
  }
)
</script>

<style scoped lang="scss">
.error-message {
  color: $color-danger;
  margin: 0;
  margin-left: 4px;
}
.ant-input-custom {
  height: 35px;
}
.ant-input-custom-disabled {
  background-color: #eaeaea;
}
.ant-input-custom-valid {
  border-color: #ff9999 !important;
}
.short_form_input {
  margin-left: 20%;
  width: 80%;
}

.ant-form-item {
  width: 100% !important;
}

.custom-label {
  justify-content: v-bind(alignDyn);
  margin-right: v-bind(marginDyn);
  margin-bottom: -4px;
}

.custom-label:after {
  /* content: ":"; */
}
.custom-no-label {
  font-size: 0.875rem;
  float: left;
  width: 100%;
  text-align: left;
  color: #fff;
  margin-bottom: v-bind(marginBottomDyn);
}
.custom-no-label:after {
  content: ':';
  font-size: 0px;
}
.require {
  min-width: 64px;
  margin-right: 0.5em;
  font-size: 12px;
  height: 32px;
  color: $white;
  background-color: #cc0000;
  text-align: center;
  line-height: 32px;
}

.no-require {
  display: none;
  margin-right: 0.5em;
  min-width: 64px;
  font-size: 12px;
  height: 32px;
  padding: 4px;
}

:deep(.ant-form-item) {
  width: 80%;
}

.ant-col:has(.require) :deep(.ant-form-item) {
  width: calc(100% - 0.5em - 64px);
}

:deep(.ant-required .ant-input) {
  background-color: #f9cb9c !important;
}

:deep(.ant-required) {
  background-color: #f9cb9c !important;
}

.suffix-image {
  width: 77%;
  height: 16px;
}

.box-image {
  margin: 0.2em 0 0 2em;
}

.input-box {
  width: 90%;
  color: #fff !important;
}

.font-black {
  color: black;
}

.login_popup_input {
  display: flex;
  align-items: center;
  margin-left: 10px;
}

.login_popup_input label {
  float: left;
  width: v-bind(widthDyn);
  color: black;
  text-align: left;
  padding-left: 3%;
  font-size: 0.8rem;
}

.login_popup_input .ant-form-item {
  width: 50%;
  float: left;
}

.login_popup_input .ant-input {
  margin-left: 0px;
  width: 50%;
  border: 2px solid #bfbfbf;
}

@media (max-width: 768px) {
  .login_popup_input label {
    width: 100%;
    font-size: 0.875rem;
    color: $black;
  }
}

:deep(.ant-form-item) {
  margin-bottom: 12px !important;
}

:deep(.ant-input:focus),
:deep(.ant-input-custom:focus),
:deep(.ant-input-affix-wrapper:focus-within),
:deep(.ant-input-affix-wrapper-focused) {
  border-color: #1890ff !important; /* Change this to your preferred ring color */
  box-shadow: 0 0 0 3px rgba(24, 144, 255, 0.2) !important; /* The 'ring' effect */
  outline: none;
}

/* Ensure the inner input of an affix wrapper (one with an icon) doesn't double-ring */
:deep(.ant-input-affix-wrapper input.ant-input:focus) {
  box-shadow: none !important;
  border-color: transparent !important;
}
</style>
